import { Column, HeaderCell, Table } from 'rsuite-table';
import 'rsuite-table/dist/css/rsuite-table.css';
import { RowDataType, SortType } from 'rsuite-table/lib/types';
import useBundleTranslation from 'i18n';
import {
    ColumnsWidth,
    ColumnType,
    DNDConfig,
    FilterValue,
    GridData,
    GridDataRow,
    LegendConfig,
    MassActionsConfig,
    MassActionsProps,
    PopupMode,
    PopupType,
    RowExtraInfoConfig,
    VisibleColumns,
} from './index';
import { useLegend } from './hooks/useLegend';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Box, Checkbox, alpha, useTheme } from '@mui/material';
import DraggableRow from './components/DraggableRow';
import buildColumn from './cell';
import MassActionCheckCell from './cell/MassActionCheckCell';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import styles from './MITable.styles';
import Legend from './components/Legend';
import SortCell from './cell/SortCell';
import { TFunction } from 'i18next';
import { useGridExtraInfo } from './hooks/useGridExtraInfo';
import SelectedItemsLabel from './components/SelectedItemsLabel';
import LoadingPlaceholder from '../loading-placeholder/LoadingPlaceholder';
import parse from 'html-react-parser';
import { FormRendererAPIType } from 'components/common/form';
import { buildTreeData } from './utils/buildTreeData';

const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return hash.toString(36);
};

export default function MITable({
    data,
    columns,
    isLoading,
    orderBy,
    order,
    onSortColumn,
    autoHeight,
    height,
    maxHeight,
    legendConfig,
    dragAndDrop,
    handleDropRow,
    handleClickRow,
    massActionsConfig,
    reloadGridData,
    onColumnResize,
    massActionsProps,
    setPopupUrl,
    setPopupConfig,
    setPopupType,
    setPopupMode,
    t,
    multilines,
    autoRowHeight,
    columnsWidth,
    customCells,
    setShowLoader,
    gridName,
    visibleColumns,
    setPopupComponent,
    setPopupComponentProps,
    rowExtraInfoConfig,
    handleScroll,
    shouldUpdateScroll,
    emptyGridMessage,
    gridReorderLoading,
    form,
    setGridDataChanged,
    uid,
    filtersValue,
    customLegend,
    gridEntityType,
    showHeader = true,
    defaultRowHeight = 32,
    zebraStriping = true,
    isTree,
    expandedRowKeys,
    defaultExpandAllRows,
    onExpandChange,
    renderTreeToggle,
    rowClassName,
    rowHeightOverride,
    virtualized = true,
}: {
    data: GridData;
    columns: Array<ColumnType>;
    isLoading?: boolean;
    orderBy?: string;
    order?: SortType;
    autoHeight?: boolean;
    height?: number;
    // Pairs with `autoHeight`: the table grows to fit its rows up to this height, then gets its own
    // scrollbar instead of growing indefinitely (rsuite-table's own documented contract for the pair).
    maxHeight?: number;
    // Default true (the long-standing hardcoded behavior). Opt out for grids whose rows can be
    // taller than the scroll viewport — rsuite's virtualized window math breaks on such rows
    // (renders them as empty shells at translate(0,0) and unmounts the rest).
    virtualized?: boolean;
    onSortColumn?: (dataKey: string, sortType?: SortType) => void;
    legendConfig?: Array<LegendConfig>;
    dragAndDrop?: DNDConfig;
    handleDropRow?: (sourceId: string, targetId: string) => void;
    handleClickRow?: (rowData: GridDataRow) => void;
    massActionsConfig: MassActionsConfig;
    reloadGridData?: () => void;
    onColumnResize?: (columnWidth?: number, dataKey?: string) => void;
    setPopupUrl?: (url: string) => void;
    setPopupConfig?: (popupConfig: PopupSettings) => void;
    setPopupComponent?: (popupComponent: string) => void;
    setPopupComponentProps?: (componentProps: { [key: string]: string }) => void;
    massActionsProps?: MassActionsProps;
    setPopupType?: (type: PopupType) => void;
    setPopupMode?: (mode: PopupMode) => void;
    t?: TFunction;
    multilines?: boolean;
    autoRowHeight?: boolean;
    columnsWidth?: ColumnsWidth;
    customCells?: { [key: string]: any };
    setShowLoader?: (value: boolean) => void;
    gridName: string;
    uid?: string;
    visibleColumns?: VisibleColumns;
    rowExtraInfoConfig?: RowExtraInfoConfig;
    handleScroll?: (scrollX: number, scrollY: number) => void;
    shouldUpdateScroll?: boolean;
    emptyGridMessage?: string;
    gridReorderLoading?: boolean;
    form?: FormRendererAPIType;
    setGridDataChanged?: (state: boolean) => void;
    filtersValue?: FilterValue;
    customLegend?: boolean;
    gridEntityType?: string;
    showHeader?: boolean;
    defaultRowHeight?: number;
    zebraStriping?: boolean;
    isTree?: boolean;
    expandedRowKeys?: Array<string | number>;
    defaultExpandAllRows?: boolean;
    onExpandChange?: (expanded: boolean, rowData: GridDataRow) => void;
    renderTreeToggle?: (expandButton: React.ReactNode, rowData?: GridDataRow, expanded?: boolean) => React.ReactNode;
    rowClassName?: (rowData: GridDataRow) => string;
    rowHeightOverride?: (rowData: any) => number;
}) {
    const { t: t2 } = useBundleTranslation();
    if (undefined === t) t = t2;

    const isDraggable = dragAndDrop?.enabled;
    const draggableConfig: any = {};
    const tableHeight = height ?? 200;
    const idColumn = dragAndDrop?.idColumn ?? 'id';
    const dataKey = massActionsConfig?.idColumn ?? 'id';

    const [errorMessage, setErrorMessage] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    const appTheme = useTheme();

    const headerRefsMap = useRef<Map<string, RefObject<HTMLDivElement>>>(new Map());

    const { extraInfoConfig } = useGridExtraInfo(data, rowExtraInfoConfig);

    const gridDataTest = useMemo(() => uid ?? gridName, [gridName, uid]);

    const rowIndexMap = useMemo(() => {
        if (!isDraggable || !handleDropRow) return null;

        const map = new Map<any, number>();
        data.forEach((row, index) => {
            map.set(row[idColumn], index);
        });
        return map;
    }, [isDraggable, handleDropRow, data, idColumn]);

    const tableData = useMemo(() => (isTree ? buildTreeData(data, dataKey) : data), [isTree, data, dataKey]);

    // In wordWrap mode rsuite-table measures row heights from the DOM as rows mount, and a
    // re-measure can never shrink them: stretched cell content (height:100%) reports the current
    // row height back. The computed pixel widths change several times after mount (initial
    // estimate, saved grid params, the final multiplier pass), so keying the remount on "widths
    // arrived" still leaves rows measured against a stale intermediate width. Key it on the widths
    // themselves instead: any change in the applied column widths remounts the table for a fresh
    // measure (this also covers a manual column resize that should shrink rows).
    const wordWrapMode = multilines && autoRowHeight;
    const tableKey = wordWrapMode ? 'widths-' + simpleHash(JSON.stringify(columnsWidth ?? {})) : undefined;

    if (isDraggable && handleDropRow && rowIndexMap) {
        draggableConfig.renderRow = (children?: React.ReactNode, rowData?: RowDataType) => {
            if (!rowData) {
                return children;
            }

            const rowIndex = rowIndexMap.get(rowData[idColumn]) ?? -1;

            return (
                <DraggableRow
                    key={rowData[idColumn]}
                    rowData={rowData}
                    idColumn={idColumn}
                    onDrop={handleDropRow}
                    rowIndex={rowIndex}
                    setIsDragging={setIsDragging}
                >
                    {children}
                </DraggableRow>
            );
        };
    }

    const errorPopupConfig: PopupSettings = useMemo(
        () => ({
            title: t('error'),
            noCancel: true,
            textOK: t('ok_button'),
        }),
        [t],
    );

    const renderEmpty = useMemo(() => {
        return emptyGridMessage ? () => <div className="rs-table-body-info">{emptyGridMessage}</div> : undefined;
    }, [emptyGridMessage]);

    const showCheckboxColumn = massActionsConfig.enabled && massActionsProps !== undefined;
    const checkedRowsKeys =
        showCheckboxColumn && massActionsProps.checkedRowsKeys ? massActionsProps.checkedRowsKeys : [];
    const { legendItems, getRowClassName } = useLegend(data, { checkedRowsKeys, dataKey }, legendConfig, customLegend);

    const rowDataIndexMap = useMemo(() => new Map(data.map((row, i) => [row, i])), [data]);

    const getRowClassNameWithZebra = useCallback(
        (rowData: any) => {
            let base = getRowClassName(rowData);
            if (rowData && rowClassName) {
                const custom = rowClassName(rowData);
                if (custom) base = base ? `${base} ${custom}` : custom;
            }
            if (!zebraStriping || !rowData) return base;
            const idx = rowDataIndexMap.get(rowData) ?? 0;
            return idx % 2 !== 0 ? (base ? `${base} mi-table-row-zebra` : 'mi-table-row-zebra') : base;
        },
        [getRowClassName, rowDataIndexMap, zebraStriping, rowClassName],
    );

    const textHeightCache = useRef<Map<string, number>>(new Map());

    useEffect(() => {
        textHeightCache.current.clear();
    }, [columnsWidth]);

    const getRowHeight = useMemo(() => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        const getFontString = (fontSize: number) => {
            const fontFamily = appTheme.typography.fontFamily || 'Inter, Arial, sans-serif';
            const fontWeight = appTheme.typography.fontWeightRegular || 400;
            return `${fontWeight} ${fontSize}px ${fontFamily}`;
        };

        const stripHtmlTags = (html: string): string => {
            let processedHtml = html.replace(/<br\s*\/?>/gi, '\n');
            const tmp = document.createElement('div');
            tmp.innerHTML = processedHtml;
            return tmp.textContent || tmp.innerText || '';
        };

        return (rowData: RowDataType) => {
            let height = defaultRowHeight;

            if (rowData && !autoRowHeight && multilines && context) {
                for (const prop in rowData) {
                    const propValue = rowData[prop];
                    if (propValue && typeof propValue === 'object') {
                        const column = columns.find((c) => c.name === prop);
                        if (!column) continue;

                        const columnWidth = columnsWidth?.[prop] || column.width || 100;
                        let calcHeight: number | undefined;

                        if (propValue.component === 'history' && propValue.config?.changes) {
                            const changes = propValue.config.changes;
                            const changesCount = Array.isArray(changes) ? changes.length : 0;

                            if (changesCount > 0) {
                                const cacheKey = `${prop}-history-${changesCount}-${columnWidth}`;
                                calcHeight = textHeightCache.current.get(cacheKey);

                                if (!calcHeight) {
                                    const fontSize = appTheme.typography.fontSize || 12;
                                    // History changes render as single ellipsized lines (no wrapping),
                                    // so the row is exactly changesCount body lines (1.5 line-height).
                                    const lineHeight = Math.round(fontSize * 1.5);
                                    calcHeight = Math.max(defaultRowHeight, changesCount * lineHeight + 14);
                                    textHeightCache.current.set(cacheKey, calcHeight);
                                }
                            }
                        } else if (propValue.config?.text && propValue.config?.multilines) {
                            const rawText = propValue.config.text;
                            let text = propValue.config.rawHtml ? stripHtmlTags(rawText) : rawText;

                            if (text && text.length > 0) {
                                const cacheKey = `${prop}-${simpleHash(text)}-${Math.round(columnWidth)}`;
                                calcHeight = textHeightCache.current.get(cacheKey);

                                if (!calcHeight) {
                                    const fontSize = appTheme.typography.fontSize || 12;
                                    const lineHeight = Math.round(fontSize * 1.5);

                                    const textLines = text.split(/\n|\r\n/);
                                    let totalLines = 0;

                                    context.font = getFontString(fontSize);

                                    // Real horizontal cell padding is 8px per side; the estimated
                                    // wrap point should track the browser's closely — canvas
                                    // measureText uses the same font, so only a small safety slack
                                    // is needed (erring high adds a blank line, erring low clips).
                                    const cellPadding = 16;
                                    const verticalPadding = 14;
                                    const availableWidth = columnWidth - cellPadding;
                                    const safetyMargin = 0.98;

                                    for (let i = 0; i < textLines.length; i++) {
                                        const line = textLines[i];
                                        if (line.length === 0) {
                                            totalLines += 1;
                                        } else {
                                            const words = line.split(/\s+/);
                                            let currentLineWidth = 0;
                                            let linesForThisText = 1;

                                            for (const word of words) {
                                                const wordWidth = context.measureText(word + ' ').width;

                                                if (currentLineWidth + wordWidth > availableWidth * safetyMargin) {
                                                    linesForThisText++;
                                                    currentLineWidth = wordWidth;
                                                } else {
                                                    currentLineWidth += wordWidth;
                                                }
                                            }

                                            totalLines += linesForThisText;
                                        }
                                    }

                                    if (propValue.config.maxLines) {
                                        totalLines = Math.min(totalLines, propValue.config.maxLines);
                                    }

                                    calcHeight = Math.max(defaultRowHeight, totalLines * lineHeight + verticalPadding);
                                    textHeightCache.current.set(cacheKey, calcHeight);
                                }
                            }
                        }

                        if (calcHeight && height < calcHeight) {
                            height = calcHeight;
                        }

                        if (textHeightCache.current.size > 1000) {
                            const firstKey = textHeightCache.current.keys().next().value;
                            if (firstKey) {
                                textHeightCache.current.delete(firstKey);
                            }
                        }
                    }
                }
            }

            return height;
        };
    }, [defaultRowHeight, autoRowHeight, multilines, columns, columnsWidth, appTheme]);

    const visibleColumnsFiltered = useMemo(() => {
        return columns.filter((column: ColumnType) => {
            return (
                !column.hidden &&
                (!visibleColumns || !visibleColumns[column.name] || visibleColumns[column.name].visible)
            );
        });
    }, [columns, visibleColumns]);

    return (
        <Box sx={styles.wrapper}>
            <SelectedItemsLabel show={false} />
            <Table
                key={tableKey}
                className={[
                    'custom-grid--rs-table',
                    isDraggable ? 'rs-table--draggable' : '',
                    handleClickRow ? 'custom-grid--clickable-rows' : '',
                    wordWrapMode ? 'rs-table--word-wrap' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
                data-test={`grid_${gridDataTest}_body`}
                virtualized={virtualized}
                sortColumn={orderBy}
                rowClassName={getRowClassNameWithZebra}
                sortType={order}
                data={tableData}
                rowHeight={
                    rowHeightOverride
                        ? // rsuite-table adds an expanded row's height only to a fixed row height, never to a computed one.
                          (rowData: any) =>
                              rowData
                                  ? rowHeightOverride(rowData) +
                                    (extraInfoConfig?.expandedRowKeys.includes(rowData[extraInfoConfig.rowKey])
                                        ? 'function' === typeof extraInfoConfig.rowExpandedHeight
                                            ? extraInfoConfig.rowExpandedHeight(rowData)
                                            : extraInfoConfig.rowExpandedHeight
                                        : 0)
                                  : defaultRowHeight
                        : multilines && autoRowHeight
                          ? undefined
                          : !multilines && extraInfoConfig
                            ? defaultRowHeight
                            : getRowHeight
                }
                wordWrap={multilines && autoRowHeight ? 'break-word' : undefined}
                onSortColumn={onSortColumn}
                height={tableHeight}
                autoHeight={autoHeight}
                maxHeight={maxHeight}
                loading={isLoading}
                headerHeight={32}
                {...draggableConfig}
                onRowClick={handleClickRow ? handleClickRow : undefined}
                {...extraInfoConfig}
                onScroll={handleScroll}
                shouldUpdateScroll={shouldUpdateScroll}
                renderEmpty={renderEmpty}
                showHeader={showHeader}
                isTree={isTree}
                rowKey={isTree ? dataKey : extraInfoConfig?.rowKey}
                expandedRowKeys={isTree ? expandedRowKeys : extraInfoConfig?.expandedRowKeys}
                defaultExpandAllRows={defaultExpandAllRows}
                onExpandChange={onExpandChange}
                renderTreeToggle={renderTreeToggle}
            >
                {isDraggable && (
                    <Column width={50} align="center" resizable={false} fixed="left">
                        <HeaderCell align={'left'}>{dragAndDrop?.hideTitle ? '' : t('column_sort_header')}</HeaderCell>
                        <SortCell tooltip={dragAndDrop?.tooltip} isDragging={isDragging} />
                    </Column>
                )}
                {showCheckboxColumn ? (
                    <Column width={24} align="right" resizable={false} fixed="left">
                        <HeaderCell
                            className="rs-table-cell--checkbox"
                            data-test={`grid_${gridDataTest}_checkbox_column_header`}
                        >
                            <Grid
                                container
                                sx={{ height: 1, alignItems: 'center', justifyContent: 'right' }}
                                className={'checkbox-holder'}
                            >
                                <Checkbox
                                    data-test={`grid_${gridDataTest}_checkbox_column_check_all`}
                                    sx={{ padding: 0 }}
                                    onChange={massActionsProps.handleCheckAllRows}
                                    checked={massActionsProps.checkAllChecked()}
                                    indeterminate={massActionsProps.checkAllIndeterminate?.() ?? false}
                                />
                            </Grid>
                        </HeaderCell>
                        <MassActionCheckCell
                            dataKey={dataKey}
                            onChange={massActionsProps.handleCheckRow}
                            checkedRowsKeys={checkedRowsKeys}
                            uid={gridDataTest}
                        />
                    </Column>
                ) : null}
                {visibleColumnsFiltered.map((column: ColumnType, index) => {
                    let existRef = headerRefsMap.current.get(column.name);

                    if (!existRef) {
                        existRef = React.createRef<HTMLDivElement>();
                        headerRefsMap.current.set(column.name, existRef);
                    }

                    const width = columnsWidth ? columnsWidth[column.name] : undefined;

                    return buildColumn(
                        column,
                        index,
                        t!,
                        gridDataTest,
                        dataKey,
                        width,
                        reloadGridData,
                        onColumnResize,
                        setPopupUrl,
                        setPopupConfig,
                        setPopupType,
                        setPopupMode,
                        customCells,
                        setShowLoader,
                        order,
                        orderBy,
                        setPopupComponent,
                        setPopupComponentProps,
                        setErrorMessage,
                        form,
                        setGridDataChanged,
                        filtersValue,
                        existRef,
                        gridEntityType,
                        isTree && index === 0,
                    );
                })}
            </Table>
            <Legend legendItems={legendItems} gridName={gridName} />
            {gridReorderLoading && <LoadingPlaceholder variant={'secondary'} relativeToParent />}
            {errorMessage > '' && (
                <Popup
                    settings={errorPopupConfig}
                    open={errorMessage > ''}
                    onHide={() => {
                        setErrorMessage('');
                    }}
                    maxWidth={'popupMd'}
                >
                    <Box>{parse(errorMessage)}</Box>
                </Popup>
            )}
        </Box>
    );
}
